/**
 * The portal's password rules, in one place.
 *
 * These mirror the Cognito user-pool policy in
 * `cloud/iac/src/components/cognito.ts` (`minimumLength: 12` + requireUppercase
 * / requireLowercase / requireNumbers / requireSymbols).
 *
 * The rules and their copy are lifted VERBATIM from the `ForceNewPassword`
 * override in `AppWithAuth.tsx` so the first-login screen and the in-app change
 * password dialog accept exactly the same set of passwords. Deliberately NOT
 * widened: `PASSWORD_SYMBOL_REGEX` is narrower than Cognito's real symbol set
 * (Cognito also accepts `[ ] { } ? " / \ , < > ' : ; | ~ ` ` and space), which
 * makes the client stricter than the server. That is fail-safe — we never
 * submit something Cognito would reject on symbols — and it is intentional.
 * `passwordPolicy.test.ts` pins the set so an accidental widening fails loudly.
 *
 * No React / Redux / MUI imports: this module is used by `AppWithAuth.tsx`,
 * which renders outside the Redux `<Provider>`.
 */

export const PASSWORD_MIN_LENGTH = 12;

/** Cognito rejects passwords longer than this. */
export const PASSWORD_MAX_LENGTH = 256;

/** Verbatim from the previous inline implementation — do not widen. */
export const PASSWORD_SYMBOL_REGEX = /[!@#\$%\^\&*\)\(+=._-]+/;

export type PasswordRuleId =
  | 'minLength'
  | 'hasUpper'
  | 'hasLower'
  | 'hasNumber'
  | 'hasSpecialChar';

export type PasswordRule = {
  id: PasswordRuleId;
  /** Shown while the rule is unmet. */
  message: string;
  test: (password: string) => boolean;
};

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'minLength',
    message: `Minimum of ${PASSWORD_MIN_LENGTH} characters`,
    test: (password) => password.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'hasUpper',
    message: 'At least one uppercase letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'hasLower',
    message: 'At least one lowercase letter',
    test: (password) => /[a-z]/.test(password),
  },
  {
    id: 'hasNumber',
    message: 'At least one digit (0-9)',
    test: (password) => /[0-9]/.test(password),
  },
  {
    id: 'hasSpecialChar',
    message: 'At least one special characters ($ % & ! etc.)',
    test: (password) => PASSWORD_SYMBOL_REGEX.test(password),
  },
] as const;

/**
 * Per-rule pass/fail, keyed by rule id. The key names match the shape the
 * `ForceNewPassword` override already used, so `Object.values(...).every(Boolean)`
 * keeps working as a submit gate.
 */
export function evaluatePassword(password: string): Record<PasswordRuleId, boolean> {
  return PASSWORD_RULES.reduce((acc, rule) => {
    acc[rule.id] = rule.test(password);
    return acc;
  }, {} as Record<PasswordRuleId, boolean>);
}

/** The rules this password does NOT yet satisfy, in display order. */
export function unmetPasswordRules(password: string): PasswordRule[] {
  return PASSWORD_RULES.filter((rule) => !rule.test(password));
}

export function isPasswordCompliant(password: string): boolean {
  return unmetPasswordRules(password).length === 0;
}
